import { CustomContext } from './configuration.model';
import { GA4ImActionType, Ga4PageGrid } from './event-data.model';
import { GA4EventType } from './general.model';

import { Cart, CartItem, Adjustment, CartTax } from '@api/cart/cart.models';
import { DroppointListsDetail } from '@api/store-locator/droppoint-lists/models/droppoint-lists.models';
import { Category } from '@core/domain/models/category.models';
import { CollectionModels } from '@features/cms-collection';
import { OrderConfirmation } from '@features/orders/order-confirmation/domain/order-confirmation.model';
import { SelectedDiscount, PaymentPayload } from '@features/payment';
import { ItemPublicModels } from '@features/products/item';
import { ProductPublicModels } from '@features/products/product';
import { ProductsArray } from '@features/products/productsArray/domain/models/productsArray.models';
import { ShippingMethod } from '@features/shipping';
import { UserShopcart } from '@features/user-shopcarts/domain/user-shopcart.model';
import { CategoryState } from '@pages/category/store/category-page.state';
import { FeelPurchasedCoupon } from '@pages/checkout/summary/domain/models/summary.model';
import { QueryResult, SearchGridView } from '@pages/search/domain/models/search.models';
import { EventFrom } from '@shared/analytics/COMMON/models/analytics-common.models';
import { ElementData } from '@shared/directives/element-observer.directive';
import { MenuCategory } from '@shared/header/models/category.model';
import { ProductViewPlace } from '@shared/product/models';
import { RelatedsPlace } from '@shared/product/related-products/infrastructure/state/related-products.state';

export enum GA4EcommerceParam {
    coupon = 'coupon',
    creative_name = 'creative_name',
    creative_slot = 'creative_slot',
    currency = 'currency',
    im_action_type = 'im_action_type',
    im_bundle_reference = 'im_bundle_reference',
    im_cart_id = 'im_cart_id',
    im_cart_number_items = 'im_cart_number_items',
    im_category_view_type = 'im_category_view_type',
    im_discount = 'im_discount',
    im_discount_type = 'im_discount_type',
    im_drop_point = 'im_drop_point',
    im_funnel_type = 'im_funnel_type',
    im_gift_card_type = 'im_gift_card_type',
    im_gift_card_used = 'im_gift_card_used',
    im_gift_card_value = 'im_gift_card_value',
    im_items_number = 'im_items_number',
    im_link_page_title = 'im_link_page_title',
    im_link_text = 'im_link_text',
    im_link_type = 'im_link_type',
    im_payment_gift_ticket = 'im_payment_gift_ticket',
    im_payment_type = 'im_payment_type',
    im_shipping_tier = 'im_shipping_tier',
    im_transaction_id = 'im_transaction_id',
    item_list_id = 'item_list_id',
    item_list_name = 'item_list_name',
    items = 'items',
    location_id = 'location_id',
    payment_type = 'payment_type',
    promotion_id = 'promotion_id',
    promotion_name = 'promotion_name',
    shipping = 'shipping',
    shipping_tier = 'shipping_tier',
    tax = 'tax',
    transaction_id = 'transaction_id',
    value = 'value',
}
export interface GA4EcommerceGroup {
    coupon: string;
    creative_name: string;
    creative_slot: string;
    currency: string;
    im_action_type: string;
    im_bundle_reference: string;
    im_cart_id: string;
    im_cart_number_items: string;
    im_category_view_type: string;
    im_discount_type: string;
    im_discount: string;
    im_drop_point: string;
    im_funnel_type: string;
    im_gift_card_type: string;
    im_gift_card_used: string;
    im_gift_card_value: string;
    im_items_number: string;
    im_link_text: string;
    im_link_type: string;
    im_payment_gift_ticket: string;
    im_payment_type: string;
    im_shipping_tier: string;
    item_list_id: string;
    item_list_name: string;
    items: GA4Item[];
    location_id: string;
    promotion_id: string;
    promotion_name: string;
    shipping: string;
    tax: string;
    transaction_id: string;
    value: string;
}

export interface GA4EcommerceValues {
    baseProduct?: ProductPublicModels.PublicProduct;
    cart?: Cart;
    categories?: MenuCategory[];
    category?: Category | MenuCategory;
    categoryProductsIdsLength?: number;
    categoryState?: CategoryState;
    collectionStory?: CollectionModels.CollectionStories;
    coupon?: FeelPurchasedCoupon[];
    customCategoryProductsIdsLength?: number;
    customContext: CustomContext;
    discount_data?: SelectedDiscount[];
    dropPointId?: string;
    elementData?: ElementData;
    eventFrom: EventFrom;
    eventType: GA4EventType;
    filteredProductsIds?: number[];
    im_cart_id?: number | string;
    im_cart_number_items?: CartItem[];
    im_discount_type?: Adjustment[];
    im_drop_point?: DroppointListsDetail;
    im_payment_gift_ticket?: boolean;
    im_payment_type?: PaymentPayload;
    im_shipping_tier?: ShippingMethod;
    imActionType?: GA4ImActionType;
    imBundleReference?: string;
    imLinkText?: string;
    isFastSint?: boolean;
    items?: CartItem[] | ProductPublicModels.PublicProduct[];
    lastBundleReference?: string;
    lastUrl?: string;
    orderConfirmation?: OrderConfirmation;
    place?: ProductViewPlace;
    productOriginData?: ProductOriginPersisting;
    productsArray?: ProductsArray;
    purchaseOrderPreviewItems?: ItemPublicModels.PublicItem[];
    purposeShopcart?: UserShopcart;
    recommenderSize?: string;
    relatedProductsPlaces?: Record<ProductViewPlace, RelatedsPlace>;
    relatedsSectionId?: string;
    searchGridType?: SearchGridView;
    searchQueryResults?: QueryResult[];
    selectedProduct?: ProductPublicModels.PublicProduct;
    shipping?: number;
    sourcePromotion?: SourcePromotionPersisting;
    tax?: CartTax[] | string;
    totalOrder?: number;
    totalProducts?: number;
}

export interface GA4Item {
    creative_name: string;
    creative_slot: string;
    discount: string;
    im_bundle_reference_item: string;
    im_page_sort: string;
    im_page_grid: Ga4PageGrid;
    im_product_color_ec: string;
    im_product_size_ec: string;
    index: string;
    isRecommended?: string | null;
    item_brand: string;
    item_category: string;
    item_category2: string;
    item_category3: string;
    item_category4: string;
    item_category5: string; // Identificador de 2 o 3 cifras de la talla del producto
    item_id: string;
    item_list_id: string;
    item_list_name: string;
    item_name: string;
    item_variant: string;
    location_id: string;
    price: string;
    promotion_id: string;
    promotion_name: string;
    quantity: string;
}

export interface ProductOriginPersisting {
    lastItemListIndex: number;
    lastItemListName: string;
    productReference: string;
    lastBundleReference: string;
    pageGrid: Ga4PageGrid;
    pageSort: string;
}

export interface SourcePromotionPersisting {
    promotionName: string;
    locationId: string;
}

export type Ga4SelectItemActionType = 'seleccionar_producto_cross' | 'seleccionar_producto' | 'ver_bundle_cross' | 'ver_bundle' | 'ver_look' | 'ver_lookbook' | 'ver_producto_look' | 'ver_producto_vistos_recientement' | 'ver_producto_wide_eyes' | 'ver_similares' | 'ver_vista_rapida_bundle' | 'ver_vista_rapida_look' | 'ver_vista_rapida_producto';
